import { useHistory } from "react-router-dom";
import styled from "styled-components";
import { MdCheckCircle } from "react-icons/md";

import {
  AnimatedTooltip,
  IAnimatedTooltipPosition,
} from "MVVM/Components/AnimatedTooltip";
import { useClipCompetitions } from "MVVM/Hooks/useCompetitionEligibility";
import { ReactComponent as CompetitionIcon } from "MVVM/Assets/Nav/nav-competition.svg";

const CompetitionEligibility = ({
  shareId,
  tooltipPosition = "top",
}: {
  shareId?: string | null;
  tooltipPosition?: IAnimatedTooltipPosition;
}) => {
  const history = useHistory();
  const competitions = useClipCompetitions(shareId);

  if (competitions.length === 0) return null;

  return (
    <AnimatedTooltip
      maxWidth={400}
      position={tooltipPosition}
      link={{ label: "", href: "" }}
      content={
        <TooltipContent>
          <TooltipHeading>Competitions</TooltipHeading>
          {competitions.map(({ competition, submitted }) =>
            submitted ? (
              <SubmittedRow
                key={competition._id}
                onClick={() => history.push(`/competitions/${competition._id}`)}
              >
                <MdCheckCircle size={14} />
                Submitted to {competition.title}
              </SubmittedRow>
            ) : (
              <CompetitionLink
                key={competition._id}
                onClick={() => history.push(`/competitions/${competition._id}`)}
              >
                Submit to {competition.title}
              </CompetitionLink>
            ),
          )}
        </TooltipContent>
      }
    >
      <Badge>
        <StyledCompetitionIcon />
        {competitions.length > 1 && <Count>{competitions.length}</Count>}
      </Badge>
    </AnimatedTooltip>
  );
};

const Badge = styled.span`
  align-items: center;
  background: ${({ theme }) => theme.colors.chalkAlpha10};
  border: 1px solid ${({ theme }) => theme.colors.envy};
  border-radius: 6px;
  cursor: pointer;
  display: inline-flex;
  flex-shrink: 0;
  gap: 4px;
  height: 24px;
  justify-content: center;
  min-width: 24px;
  padding: 0 6px;
`;

const StyledCompetitionIcon = styled(CompetitionIcon)`
  height: 14px;
  width: 16px;

  path {
    stroke: ${({ theme }) => theme.colors.envy};
  }
`;

const Count = styled.span`
  color: ${({ theme }) => theme.colors.ice};
  font-size: 12px;
  font-weight: 700;
`;

const TooltipContent = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
  text-align: left;
`;

const TooltipHeading = styled.span`
  color: ${({ theme }) => theme.colors.chalk};
  font-size: 11px;
  font-weight: 600;
  letter-spacing: 0.04em;
  text-transform: uppercase;
`;

const CompetitionLink = styled.span`
  color: ${({ theme }) => theme.colors.ice};
  cursor: pointer;
  font-size: 14px;
  font-weight: 600;
  max-width: 100%;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;

  &:hover {
    color: ${({ theme }) => theme.colors.envy};
  }
`;

const SubmittedRow = styled.span`
  align-items: center;
  color: ${({ theme }) => theme.colors.chalk};
  cursor: pointer;
  display: flex;
  font-size: 14px;
  font-weight: 600;
  gap: 6px;
  max-width: 100%;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;

  svg {
    color: ${({ theme }) => theme.colors.envy};
    flex-shrink: 0;
  }

  &:hover {
    color: ${({ theme }) => theme.colors.ice};
  }
`;

export { CompetitionEligibility };
